import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ErrorService, LayoutService } from '@core/services';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { Observable, Subject } from 'rxjs';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { ActionNotificationInfo, PostOverrideNotificationDate, StiiraError } from '@core/models';
import { indicate, keepDialogOpen } from '@shared/helpers';

@Component({
    selector: 'app-override-notification-date',
    templateUrl: './override-notification-date.component.html',
    styleUrl: './override-notification-date.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OverrideNotificationDateComponent {
  static readonly componentName = 'OverrideNotificationDateComponent';
  
  public form: UntypedFormGroup;
  public isSubmitting$: Subject<boolean> = new Subject<boolean>();
  // restore and save both go through onSubmit and share isSubmitting$, so this says which one is running and the template puts
  // the spinner in that button
  public submittingAction: 'restore' | 'save';
  public position = position;
  public onDragEnded = onDragEnded;
  public sysText: any;

  private formInitValues: any;

  public isMobile$: Observable<boolean>;

  get noChanges(): boolean {
    return JSON.stringify(this.form.value) === JSON.stringify(this.formInitValues);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      notifInfo: ActionNotificationInfo,
      isWarning: boolean
    },
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<OverrideNotificationDateComponent>,
    private dialog: MatDialog,
    private layoutService: LayoutService,
    private errorService: ErrorService,
    private service: LeaveAdminService,
    private store: LeaveAdminStoreService
  ) { 
    this.form = this.fb.group({
      date: new FormControl(null),
      comments: new FormControl(null)
    });
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    this.sysText = this.store.sysText[this.store.templates.actionItems];

    if (this.data.isWarning){
      this.form.patchValue({
        date: this.data.notifInfo.warningOverriddenOn != null ? this.data.notifInfo.warningDate : null,
        comments: this.data.notifInfo.warningOverrideComment
      });
    } else {
      this.form.patchValue({
        date: this.data.notifInfo.dueOverriddenOn != null ? this.data.notifInfo.dueDate : null,
        comments: this.data.notifInfo.dueOverrideComment
      });
    }

    this.formInitValues = { ...this.form.value };
  }

  public onSubmit(restoreDefaults: boolean = false): void {
    this.submittingAction = restoreDefaults ? 'restore' : 'save';
    const dto: PostOverrideNotificationDate = {
      caseId: this.store.caseDetails.leaveInformation.caseId,
      actionNotificationId: this.data.notifInfo.id,
      restoreDefaults: restoreDefaults,
      isWarning: this.data.isWarning,
      date: this.form.controls.date.value,
      comments: this.form.controls.comments.value
    }
    this.service.postOverrideNotificationDate(dto)
      .pipe(
        indicate(this.isSubmitting$),
        keepDialogOpen(this.dialogRef)
      )
      .subscribe((res) => {
          this.store.caseDetails = res;
          this.dialogRef.close();
        }, (err: StiiraError) => {
          this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors);
        }
      );
  }

  public close(canNavigate: boolean): void {
    if (canNavigate) {
      this.dialogRef.close();
    } else {
      if (this.noChanges) {
        this.dialogRef.close();
      } else {
        this.openUnsavedChangesDialog();
      }
    }
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.store.sysText[this.store.templates.unsavedChanges]
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }
}